import { useEffect, useState, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate } from "react-router-dom";
import { LogoLoader } from "../components/LogoLoader";
import { ArrowLeft, ChevronDown, ChevronRight, RotateCcw } from "lucide-react";
import i18n from "../i18n";

function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = useCallback(
    (key: string, options?: Record<string, unknown>) =>
      options != null
        ? String((i18n.t as any)(key, options))
        : String((i18n.t as any)(key)),
    []
  );
  return { t, lng };
}

type Player = {
  id?: string;
  firstName: string;
  lastName: string;
  number: number;
  position: string;
};

type StatRow = {
  match_id: string;
  player_number: number;
  player_name: string;
  position: string;
  points: number;
  assists: number;
  rebounds: number;
  steals: number;
  blocks: number;
  turnovers: number;
  shots_attempted: number;
  shots_made: number;
  ft_attempted: number;
  ft_made: number;
  three_attempted: number;
  three_made: number;
};

type MatchSnapshot = {
  id: string;
  opponent: string;
  date: string;
  location?: string;
  score?: string;
  stats: StatRow[];
};

type TeamSnapshot = {
  id: string;
  team_name: string;
  league?: string | null;
  players: Player[];
};

type ArchiveData = {
  team: TeamSnapshot;
  matches: MatchSnapshot[];
};

type Archive = {
  id: string;
  team_id: string;
  team_name: string;
  archived_at: string;
  data: ArchiveData;
};

export const ArchivedTeams = () => {
  const { t, lng } = useI18nSafe();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [archives, setArchives] = useState<Archive[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expandedMatchId, setExpandedMatchId] = useState<string | null>(null);

  type AttendanceMap = Record<string, { present: number; total: number }>;
  const [attendance, setAttendance] = useState<AttendanceMap>({});
  const [attendanceTotal, setAttendanceTotal] = useState(0);

  const locale = lng === "fr" ? "fr-FR" : "en-US";

  useEffect(() => {
    const fetchArchives = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      const { data, error } = await supabase
        .from("team_archives")
        .select("id, team_id, team_name, archived_at, data")
        .eq("user_id", user.id)
        .order("archived_at", { ascending: false });

      if (error) console.error("[ArchivedTeams] fetch error:", error);
      const rows = (data as Archive[]) || [];
      setArchives(rows);
      if (rows.length > 0) setSelectedId(rows[0].id);
      setLoading(false);
    };
    fetchArchives();
  }, []);

  useEffect(() => {
    const archive = archives.find(a => a.id === selectedId) ?? null;
    if (!archive?.team_id) {
      setAttendance({});
      setAttendanceTotal(0);
      return;
    }
    (async () => {
      const { data: trainings } = await supabase
        .from("trainings")
        .select("id")
        .eq("team_id", archive.team_id);

      const trainingIds = (trainings || []).map((tr: any) => tr.id);
      setAttendanceTotal(trainingIds.length);

      if (trainingIds.length === 0) {
        setAttendance({});
        return;
      }

      const { data: rows } = await supabase
        .from("training_attendance")
        .select("player_json_id, was_present")
        .in("training_id", trainingIds);

      const map: AttendanceMap = {};
      (rows || []).forEach((r: any) => {
        if (!r.player_json_id) return;
        if (!map[r.player_json_id]) map[r.player_json_id] = { present: 0, total: 0 };
        map[r.player_json_id].total += 1;
        if (r.was_present) map[r.player_json_id].present += 1;
      });
      setAttendance(map);
    })();
  }, [selectedId, archives]);

  const [restoring, setRestoring] = useState(false);

  const handleRestore = useCallback(async (archive: Archive) => {
    if (!window.confirm(t("archivedteams:restore_confirm"))) return;
    setRestoring(true);

    const { error: updateError } = await supabase
      .from("teams")
      .update({ archived: false })
      .eq("id", archive.team_id);

    if (updateError) {
      console.error("[restore] teams update error:", updateError);
      alert(t("archivedteams:restore_error"));
      setRestoring(false);
      return;
    }

    await supabase.from("team_archives").delete().eq("id", archive.id);

    const remaining = archives.filter(a => a.id !== archive.id);
    setArchives(remaining);
    setSelectedId(remaining.length > 0 ? remaining[0].id : null);
    setRestoring(false);
    alert(t("archivedteams:restore_success"));
  }, [archives, t]);

  const formatDate = useCallback((dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString(locale, {
        day: "2-digit", month: "short", year: "numeric",
      });
    } catch { return dateStr; }
  }, [locale]);

  if (loading) return <LogoLoader />;

  const selectedArchive = archives.find(a => a.id === selectedId) ?? null;

  type SeasonStat = StatRow & { games: number };
  const seasonStats: SeasonStat[] = (() => {
    if (!selectedArchive) return [];
    const byPlayer: Record<string, SeasonStat> = {};
    (selectedArchive.data?.matches ?? []).forEach(match => {
      (match.stats ?? []).forEach((s: StatRow) => {
        const key = `${s.player_number}-${s.player_name}`;
        if (!byPlayer[key]) {
          byPlayer[key] = { ...s, games: 1 };
        } else {
          byPlayer[key].points += s.points ?? 0;
          byPlayer[key].assists += s.assists ?? 0;
          byPlayer[key].rebounds += s.rebounds ?? 0;
          byPlayer[key].steals += s.steals ?? 0;
          byPlayer[key].blocks += s.blocks ?? 0;
          byPlayer[key].turnovers += s.turnovers ?? 0;
          byPlayer[key].shots_attempted += s.shots_attempted ?? 0;
          byPlayer[key].shots_made += s.shots_made ?? 0;
          byPlayer[key].ft_attempted += s.ft_attempted ?? 0;
          byPlayer[key].ft_made += s.ft_made ?? 0;
          byPlayer[key].three_attempted += s.three_attempted ?? 0;
          byPlayer[key].three_made += s.three_made ?? 0;
          byPlayer[key].games += 1;
        }
      });
    });
    return Object.values(byPlayer).sort((a, b) => (a.player_number ?? 0) - (b.player_number ?? 0));
  })();

  return (
    <section className="flex flex-col items-center p-6 min-h-screen bg-gray-50 dark:bg-gray-950 dark:text-gray-100">
      <div className="w-full max-w-4xl mb-6">
        <button
          onClick={() => navigate("/myteam")}
          className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("archivedteams:back")}
        </button>
        <h1 className="text-2xl font-bold text-orange-500 dark:text-orange-400">
          {t("archivedteams:title")}
        </h1>
      </div>

      {archives.length === 0 ? (
        <p className="text-gray-600 dark:text-gray-400">{t("archivedteams:no_archives")}</p>
      ) : (
        <div className="w-full max-w-4xl flex flex-col lg:flex-row gap-6">
          {/* Liste des archives */}
          <div className="lg:w-64 flex-shrink-0 flex flex-col gap-2">
            {archives.map(archive => {
              const matchCount = archive.data?.matches?.length ?? 0;
              const playerCount = archive.data?.team?.players?.length ?? 0;
              const isSelected = archive.id === selectedId;
              return (
                <button
                  key={archive.id}
                  onClick={() => { setSelectedId(archive.id); setExpandedMatchId(null); }}
                  className={`text-left p-4 rounded-xl border transition ${
                    isSelected
                      ? "border-orange-400 bg-orange-50 dark:bg-orange-950/20"
                      : "border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 hover:border-orange-300"
                  }`}
                >
                  <p className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {archive.team_name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {formatDate(archive.archived_at)}
                  </p>
                  <div className="flex gap-3 mt-2 text-xs text-gray-600 dark:text-gray-400">
                    <span>{t("archivedteams:n_players", { count: playerCount })}</span>
                    <span>{t("archivedteams:n_matches", { count: matchCount })}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Détail de l'archive sélectionnée */}
          {selectedArchive && (
            <div className="flex-1 min-w-0 flex flex-col gap-6">
              {/* Info équipe */}
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-bold text-orange-500 dark:text-orange-400 mb-1">
                      {selectedArchive.data.team.team_name}
                    </h2>
                    {selectedArchive.data.team.league && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t("archivedteams:league_label")} {selectedArchive.data.team.league}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      {t("archivedteams:archived_on")} {formatDate(selectedArchive.archived_at)}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRestore(selectedArchive)}
                    disabled={restoring}
                    className="flex-shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium
                               bg-orange-50 dark:bg-orange-950/30 text-orange-600 dark:text-orange-400
                               border border-orange-200 dark:border-orange-800
                               hover:bg-orange-100 dark:hover:bg-orange-950/50 transition disabled:opacity-50"
                  >
                    <RotateCcw className="w-4 h-4" />
                    {t("archivedteams:restore_btn")}
                  </button>
                </div>
              </div>

              {/* Joueurs + Présence : grille 2 colonnes sur md+ */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">

              {/* Joueurs */}
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
                  <h3 className="font-semibold text-gray-800 dark:text-gray-200">
                    {t("archivedteams:section_players")} ({selectedArchive.data.team.players.length})
                  </h3>
                </div>
                {selectedArchive.data.team.players.length === 0 ? (
                  <p className="px-5 py-4 text-sm text-gray-500">{t("archivedteams:no_players")}</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="p-3 text-left text-gray-700 dark:text-gray-300">#</th>
                          <th className="p-3 text-left text-gray-700 dark:text-gray-300">{t("archivedteams:col_name")}</th>
                          <th className="p-3 text-left text-gray-700 dark:text-gray-300">{t("archivedteams:col_position")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...selectedArchive.data.team.players]
                          .sort((a, b) => (a.number ?? 0) - (b.number ?? 0))
                          .map((player, i) => (
                            <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                              <td className="p-3 font-mono text-gray-700 dark:text-gray-300">{player.number}</td>
                              <td className="p-3 text-gray-900 dark:text-gray-100">{player.lastName} {player.firstName}</td>
                              <td className="p-3 text-gray-600 dark:text-gray-400">{player.position}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Présence aux entraînements */}
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
                  <h3 className="font-semibold text-gray-800 dark:text-gray-200">
                    {t("archivedteams:section_attendance")}
                  </h3>
                </div>
                {attendanceTotal === 0 ? (
                  <p className="px-5 py-4 text-sm text-gray-500">{t("archivedteams:no_attendance")}</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="p-3 text-left text-gray-700 dark:text-gray-300">#</th>
                          <th className="p-3 text-left text-gray-700 dark:text-gray-300">{t("archivedteams:col_name")}</th>
                          <th className="p-3 text-right text-gray-700 dark:text-gray-300">{t("archivedteams:col_present")}</th>
                          <th className="p-3 text-right text-gray-700 dark:text-gray-300">{t("archivedteams:col_trainings")}</th>
                          <th className="p-3 text-right text-gray-700 dark:text-gray-300">{t("archivedteams:col_rate")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...selectedArchive.data.team.players]
                          .sort((a, b) => (a.number ?? 0) - (b.number ?? 0))
                          .map((player, i) => {
                            const pid = player.id ? String(player.id) : null;
                            const att = pid ? (attendance[pid] ?? { present: 0, total: 0 }) : { present: 0, total: 0 };
                            const rate = att.total > 0
                              ? `${Math.round((att.present / att.total) * 100)} %`
                              : "–";
                            return (
                              <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                                <td className="p-3 font-mono text-gray-600 dark:text-gray-400">{player.number}</td>
                                <td className="p-3 text-gray-900 dark:text-gray-100">{player.lastName} {player.firstName}</td>
                                <td className="p-3 text-right font-semibold text-orange-600 dark:text-orange-400">{att.present}</td>
                                <td className="p-3 text-right text-gray-600 dark:text-gray-400">{attendanceTotal}</td>
                                <td className="p-3 text-right text-gray-700 dark:text-gray-300">{rate}</td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              </div>{/* end grid joueurs+présence */}

              {/* Stats de la saison */}
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
                  <h3 className="font-semibold text-gray-800 dark:text-gray-200">
                    {t("archivedteams:section_season_stats")}
                  </h3>
                </div>
                {seasonStats.length === 0 ? (
                  <p className="px-5 py-4 text-sm text-gray-500">{t("archivedteams:no_season_stats")}</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr className="text-gray-500 dark:text-gray-400">
                          <th className="px-3 py-2 text-left">#</th>
                          <th className="px-3 py-2 text-left">{t("archivedteams:col_name")}</th>
                          <th className="px-3 py-2 text-right">{t("archivedteams:col_games")}</th>
                          <th className="px-3 py-2 text-right">{t("archivedteams:col_pts")}</th>
                          <th className="px-3 py-2 text-right">{t("archivedteams:col_avg")}</th>
                          <th className="px-3 py-2 text-right">{t("archivedteams:col_ast")}</th>
                          <th className="px-3 py-2 text-right">{t("archivedteams:col_reb")}</th>
                          <th className="px-3 py-2 text-right">{t("archivedteams:col_stl")}</th>
                          <th className="px-3 py-2 text-right">{t("archivedteams:col_blk")}</th>
                          <th className="px-3 py-2 text-right">{t("archivedteams:col_to")}</th>
                          <th className="px-3 py-2 text-right">{t("archivedteams:col_fg")}</th>
                          <th className="px-3 py-2 text-right">{t("archivedteams:col_fg_pct")}</th>
                          <th className="px-3 py-2 text-right">{t("archivedteams:col_ft")}</th>
                          <th className="px-3 py-2 text-right">{t("archivedteams:col_tp")}</th>
                          <th className="px-3 py-2 text-right">{t("archivedteams:col_eval")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {seasonStats.map((s, i) => {
                          const evalVal = (
                            (s.points + s.rebounds + s.assists + s.steals + s.blocks
                              - s.turnovers + s.shots_made - s.shots_attempted) / s.games
                          ).toFixed(1);
                          return (
                            <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                              <td className="px-3 py-2 font-mono text-gray-600 dark:text-gray-400">{s.player_number}</td>
                              <td className="px-3 py-2 text-gray-900 dark:text-gray-100 whitespace-nowrap">{s.player_name}</td>
                              <td className="px-3 py-2 text-right">{s.games}</td>
                              <td className="px-3 py-2 text-right font-semibold text-orange-600 dark:text-orange-400">{s.points}</td>
                              <td className="px-3 py-2 text-right">{(s.points / s.games).toFixed(1)}</td>
                              <td className="px-3 py-2 text-right">{s.assists}</td>
                              <td className="px-3 py-2 text-right">{s.rebounds}</td>
                              <td className="px-3 py-2 text-right">{s.steals}</td>
                              <td className="px-3 py-2 text-right">{s.blocks}</td>
                              <td className="px-3 py-2 text-right">{s.turnovers}</td>
                              <td className="px-3 py-2 text-right">{s.shots_made}/{s.shots_attempted}</td>
                              <td className="px-3 py-2 text-right">
                                {s.shots_attempted > 0 ? `${((s.shots_made / s.shots_attempted) * 100).toFixed(1)}%` : "–"}
                              </td>
                              <td className="px-3 py-2 text-right">{s.ft_made}/{s.ft_attempted}</td>
                              <td className="px-3 py-2 text-right">{s.three_made}/{s.three_attempted}</td>
                              <td className="px-3 py-2 text-right">{evalVal}</td>
                            </tr>
                          );
                        })}
                        <tr className="border-t-2 border-orange-200 dark:border-orange-900 bg-orange-50 dark:bg-orange-950/20 font-semibold">
                          <td colSpan={3} className="px-3 py-2 text-gray-700 dark:text-gray-300">
                            {t("archivedteams:total")}
                          </td>
                          <td className="px-3 py-2 text-right text-orange-600 dark:text-orange-400">
                            {seasonStats.reduce((sum, s) => sum + s.points, 0)}
                          </td>
                          <td className="px-3 py-2 text-right">–</td>
                          <td className="px-3 py-2 text-right">{seasonStats.reduce((sum, s) => sum + s.assists, 0)}</td>
                          <td className="px-3 py-2 text-right">{seasonStats.reduce((sum, s) => sum + s.rebounds, 0)}</td>
                          <td className="px-3 py-2 text-right">{seasonStats.reduce((sum, s) => sum + s.steals, 0)}</td>
                          <td className="px-3 py-2 text-right">{seasonStats.reduce((sum, s) => sum + s.blocks, 0)}</td>
                          <td className="px-3 py-2 text-right">{seasonStats.reduce((sum, s) => sum + s.turnovers, 0)}</td>
                          <td className="px-3 py-2 text-right">
                            {seasonStats.reduce((sum, s) => sum + s.shots_made, 0)}/{seasonStats.reduce((sum, s) => sum + s.shots_attempted, 0)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {(() => {
                              const made = seasonStats.reduce((sum, s) => sum + s.shots_made, 0);
                              const att = seasonStats.reduce((sum, s) => sum + s.shots_attempted, 0);
                              return att > 0 ? `${((made / att) * 100).toFixed(1)}%` : "–";
                            })()}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {seasonStats.reduce((sum, s) => sum + s.ft_made, 0)}/{seasonStats.reduce((sum, s) => sum + s.ft_attempted, 0)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {seasonStats.reduce((sum, s) => sum + s.three_made, 0)}/{seasonStats.reduce((sum, s) => sum + s.three_attempted, 0)}
                          </td>
                          <td className="px-3 py-2 text-right">–</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Matchs */}
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
                  <h3 className="font-semibold text-gray-800 dark:text-gray-200">
                    {t("archivedteams:section_matches")} ({selectedArchive.data.matches.length})
                  </h3>
                </div>
                {selectedArchive.data.matches.length === 0 ? (
                  <p className="px-5 py-4 text-sm text-gray-500">{t("archivedteams:no_matches")}</p>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-gray-800">
                    {[...selectedArchive.data.matches]
                      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                      .map(match => {
                        const isExpanded = expandedMatchId === match.id;
                        const statsRows: StatRow[] = match.stats ?? [];
                        const teamTotal = statsRows.reduce((sum, s) => sum + (s.points ?? 0), 0);
                        const locationLabel = match.location === "domicile"
                          ? t("archivedteams:loc_home")
                          : match.location === "extérieur"
                          ? t("archivedteams:loc_away")
                          : null;

                        return (
                          <div key={match.id}>
                            <button
                              className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition text-left"
                              onClick={() => setExpandedMatchId(isExpanded ? null : match.id)}
                            >
                              <div>
                                <p className="font-semibold text-gray-900 dark:text-gray-100">
                                  {t("archivedteams:vs")} {match.opponent}
                                </p>
                                <div className="flex flex-wrap gap-3 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                  <span>{formatDate(match.date)}</span>
                                  {locationLabel && <span>{locationLabel}</span>}
                                  {match.score && (
                                    <span className="font-medium text-gray-700 dark:text-gray-300">{match.score}</span>
                                  )}
                                  {statsRows.length > 0 && (
                                    <span>{t("archivedteams:team_pts", { pts: teamTotal })}</span>
                                  )}
                                </div>
                              </div>
                              {isExpanded
                                ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              }
                            </button>

                            {isExpanded && statsRows.length === 0 && (
                              <div className="px-5 py-3 text-sm text-gray-500 bg-gray-50 dark:bg-gray-800/30 border-t border-gray-100 dark:border-gray-800">
                                {t("archivedteams:no_stats")}
                              </div>
                            )}

                            {isExpanded && statsRows.length > 0 && (
                              <div className="overflow-x-auto bg-gray-50 dark:bg-gray-800/30 border-t border-gray-100 dark:border-gray-800">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-gray-500 dark:text-gray-400">
                                      <th className="px-3 py-2 text-left">#</th>
                                      <th className="px-3 py-2 text-left">{t("archivedteams:col_name")}</th>
                                      <th className="px-3 py-2 text-right">Pts</th>
                                      <th className="px-3 py-2 text-right">Pd</th>
                                      <th className="px-3 py-2 text-right">Reb</th>
                                      <th className="px-3 py-2 text-right">Int</th>
                                      <th className="px-3 py-2 text-right">Ctr</th>
                                      <th className="px-3 py-2 text-right">TO</th>
                                      <th className="px-3 py-2 text-right">T2</th>
                                      <th className="px-3 py-2 text-right">T3</th>
                                      <th className="px-3 py-2 text-right">LF</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {[...statsRows]
                                      .sort((a, b) => (a.player_number ?? 0) - (b.player_number ?? 0))
                                      .map((s, i) => (
                                        <tr key={i} className="border-t border-gray-200 dark:border-gray-700">
                                          <td className="px-3 py-2 font-mono text-gray-600 dark:text-gray-400">{s.player_number}</td>
                                          <td className="px-3 py-2 text-gray-900 dark:text-gray-100 whitespace-nowrap">{s.player_name}</td>
                                          <td className="px-3 py-2 text-right font-semibold text-orange-600 dark:text-orange-400">{s.points}</td>
                                          <td className="px-3 py-2 text-right">{s.assists}</td>
                                          <td className="px-3 py-2 text-right">{s.rebounds}</td>
                                          <td className="px-3 py-2 text-right">{s.steals}</td>
                                          <td className="px-3 py-2 text-right">{s.blocks}</td>
                                          <td className="px-3 py-2 text-right">{s.turnovers}</td>
                                          <td className="px-3 py-2 text-right">{s.shots_made}/{s.shots_attempted}</td>
                                          <td className="px-3 py-2 text-right">{s.three_made}/{s.three_attempted}</td>
                                          <td className="px-3 py-2 text-right">{s.ft_made}/{s.ft_attempted}</td>
                                        </tr>
                                      ))}
                                    <tr className="border-t-2 border-orange-200 dark:border-orange-900 bg-orange-50 dark:bg-orange-950/20 font-semibold">
                                      <td colSpan={2} className="px-3 py-2 text-gray-700 dark:text-gray-300">
                                        {t("archivedteams:total")}
                                      </td>
                                      <td className="px-3 py-2 text-right text-orange-600 dark:text-orange-400">{teamTotal}</td>
                                      <td className="px-3 py-2 text-right">{statsRows.reduce((s, r) => s + (r.assists ?? 0), 0)}</td>
                                      <td className="px-3 py-2 text-right">{statsRows.reduce((s, r) => s + (r.rebounds ?? 0), 0)}</td>
                                      <td className="px-3 py-2 text-right">{statsRows.reduce((s, r) => s + (r.steals ?? 0), 0)}</td>
                                      <td className="px-3 py-2 text-right">{statsRows.reduce((s, r) => s + (r.blocks ?? 0), 0)}</td>
                                      <td className="px-3 py-2 text-right">{statsRows.reduce((s, r) => s + (r.turnovers ?? 0), 0)}</td>
                                      <td className="px-3 py-2 text-right">
                                        {statsRows.reduce((s, r) => s + (r.shots_made ?? 0), 0)}/{statsRows.reduce((s, r) => s + (r.shots_attempted ?? 0), 0)}
                                      </td>
                                      <td className="px-3 py-2 text-right">
                                        {statsRows.reduce((s, r) => s + (r.three_made ?? 0), 0)}/{statsRows.reduce((s, r) => s + (r.three_attempted ?? 0), 0)}
                                      </td>
                                      <td className="px-3 py-2 text-right">
                                        {statsRows.reduce((s, r) => s + (r.ft_made ?? 0), 0)}/{statsRows.reduce((s, r) => s + (r.ft_attempted ?? 0), 0)}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};
